import { Chip, Paper, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { shadows } from 'c1ui/theme/C1Theme'
import { C1Icon } from 'components/core/icons/C1Icon'
import { SafeMarkdown } from 'components/core/markdown'
import { Check } from 'lucide-react'
import { Automation } from 'pbts/c1/api/automations/v1/automations'
import { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

const getTriggerTypeName = (automation: Automation): string | null => {
    if (!automation.triggers?.length) return null
    const trigger = automation.triggers[0]
    switch (trigger.kind.oneofKind) {
        case 'manual':
            return 'Manual'
        case 'schedule':
            return 'Schedule'
        case 'webhook':
            return 'Webhook'
        case 'userCreated':
            return 'User Created'
        case 'userProfileChange':
            return 'User Profile Change'
        case 'appUserCreated':
            return 'App User Created'
        case 'appUserUpdated':
            return 'App User Updated'
        case 'grantFound':
            return 'Grant Found'
        case 'grantDeleted':
            return 'Grant Deleted'
        case 'usageBasedRevocation':
            return 'Usage Based Revocation'
        case 'form':
            return 'Form'
        case 'scheduleAppUser':
            return 'Schedule (per App User)'
        case 'accessConflict':
            return 'Access Conflict'
        default:
            return null
    }
}

type AutomationActionItemProps = {
    automation: Automation
    explanation: string
    isNew: boolean
    onApply?: () => void
}

/**
 * Renders an automation draft result from the AI assistant.
 * Displays the automation header, trigger/steps summary, explanation, and an "Apply" button.
 */
export const AutomationActionItem: FC<AutomationActionItemProps> = ({
    automation,
    explanation,
    isNew,
    onApply,
}) => {
    const intl = useIntl()

    // Don't render if there's nothing to show and no action
    if (!automation.displayName && !onApply) {
        return null
    }

    const triggerType = getTriggerTypeName(automation)
    const stepCount = automation.automationSteps?.length ?? 0

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                bgcolor: 'common.white',
                borderRadius: C1BorderRadius.large,
                boxShadow: shadows.default,
            }}
        >
            <Stack spacing={2}>
                {/* Header with automation icon and name */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                        label={intl.formatMessage({
                            id: 'automation_chip_label',
                            defaultMessage: 'Automation',
                        })}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                    <Typography variant="h6" component="span">
                        {automation.displayName || intl.formatMessage({
                            id: 'copilot_automation_untitled',
                            defaultMessage: 'Untitled automation',
                        })}
                    </Typography>
                </Stack>

                {/* Description if present */}
                {automation.description && (
                    <Typography color="text.secondary">
                        {automation.description}
                    </Typography>
                )}

                {/* Trigger and steps summary */}
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {triggerType && (
                        <Chip
                            label={intl.formatMessage(
                                {
                                    id: 'copilot_automation_trigger_chip',
                                    defaultMessage: 'Trigger: {type}',
                                },
                                { type: triggerType },
                            )}
                            size="small"
                            variant="outlined"
                        />
                    )}
                    <Chip
                        label={intl.formatMessage(
                            {
                                id: 'copilot_automation_steps_chip',
                                defaultMessage: '{count} {count, plural, one {step} other {steps}}',
                            },
                            { count: stepCount },
                        )}
                        size="small"
                        variant="outlined"
                    />
                    {automation.enabled && (
                        <Chip
                            label={intl.formatMessage({
                                id: 'copilot_automation_enabled_chip',
                                defaultMessage: 'Enabled',
                            })}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    )}
                </Stack>

                {/* Explanation from the AI */}
                {explanation && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{
                            '& p': {
                                margin: 0,
                            },
                            '& p:not(:last-child)': {
                                marginBottom: 1,
                            },
                        }}
                    >
                        <SafeMarkdown>{explanation}</SafeMarkdown>
                    </Typography>
                )}

                {/* Apply button */}
                {onApply && (
                    <C1Button
                        variant="contained"
                        color="primary"
                        onClick={onApply}
                        startIcon={<C1Icon icon={Check} />}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {isNew ? (
                            <FormattedMessage
                                id="copilot_automation_apply_create"
                                defaultMessage="Apply to editor"
                            />
                        ) : (
                            <FormattedMessage
                                id="copilot_automation_apply_edit"
                                defaultMessage="Apply changes"
                            />
                        )}
                    </C1Button>
                )}
            </Stack>
        </Paper>
    )
}
